// based on https://github.com/chriso/validator.js
import validator from 'validator';
import { t } from 'i18next';
import {
  getTotalWeight,
  getZip,
  isAmericanAddress,
  numberWithCommas,
} from './func';
import { Divisions, AMPM, UnitsOfMeasure } from './enums';
import { getAbsoluteDifferenceInDays, isValidDateRange, MAX_DATE_RANGE_DAYS } from './dateTime';
import { streetAddressExceptions } from './constants';
import { isInt } from './numbers';
import { formatString, isNullOrWhiteSpace } from './strings';

export const validate = (validation, value, options, errorMessage) => {
  if (!value) {
    return undefined;
  }

  if (options) {
    if (!validation(value, options)) {
      return errorMessage;
    }
  } else if (!validation(value)) {
    return errorMessage;
  }

  return undefined;
};

export const isLength = (length) => (value) => (value && value.length !== length ? t('MustBeExactChar', { length }) : undefined);

export const isDigitLength = (length) => (value) => (value && value.length !== length ? t('MustBeExactDigit', { length }) : undefined);

export const maxLength = (max) => (value) => (value && value.length > max ? t('MustBeEqualOrLessChar', { max }) : undefined);

export const isRequired = (value) => {
  if (value) {
    return undefined;
  }

  return t('Required');
};

export const isRequiredTrimmed = (value) => {
  if (value && typeof value === 'string' && value.trim()) {
    return undefined;
  }

  return t('Required');
};

export const isUSZipError = (value) => {
  const regex = /^\d{5}(-\d{4})?$/;
  if (regex.test(value)) {
    return t('NoServiceOutsideCanada');
  }

  return null;
};

export const hasSomeSelected = (value) => {
  if (value && value.filter((v) => !isNullOrWhiteSpace(v)).length > 0) {
    return undefined;
  }

  return t('MustSelectAtLeastOne');
};

export const anyOneRequiredInGroup = (value, allValues) => {
  if (Object.keys(allValues).length === 0) {
    return t('AtleastOneRequired');
  }

  if ((allValues.searchByUserEmail && allValues.searchByUserEmail === '')
    || (allValues.searchByCompany && allValues.searchByCompany === '')
    || (allValues.searchByAccountNumber && allValues.searchByAccountNumber === '')) {
    return t('FieldCannotBeEmpty');
  }

  return undefined;
};

export const isPhone = (value) => {
  if (!value) {
    return undefined;
  }

  if (!validator.isMobilePhone(value, 'en-CA', 'en-US')) {
    return t('InvalidPhoneNumber');
  }

  return undefined;
};

export const isPhoneExtension = (value) => {
  if (!value || value.length === 0) {
    return undefined;
  }

  const regexPattern = /^[0-9]*$/;
  if (!regexPattern.test(value)) {
    return t('InvalidPhoneExtension');
  }

  return undefined;
};

export const isFaxNumber = (value) => {
  if (!value) {
    return undefined;
  }

  if (!validator.isMobilePhone(value, 'en-CA', 'en-US')) {
    return t('InvalidFaxNumber');
  }

  return undefined;
};

export const isEmail = (value) => validate(validator.isEmail, value, null, t('InvalidEmailAddress'));

export const isInRange = (min, max) => (value) => {
  if (value) {
    if (parseInt(value, 10) < min || parseInt(value, 10) > max) {
      return formatString(t('RangeError'), min, max);
    }
  }

  return undefined;
};

export const validateIntInRange = (min, max) => (value) => {
  if (value) {
    if (!isInt(value)) {
      return t('NotAnIntError');
    }

    if (parseInt(value, 10) < min || parseInt(value, 10) > max) {
      return formatString(t('RangeError'), min, max);
    }
  }

  return undefined;
};

export const addressValidate = (address, formData, props) => {
  const streetAddressException = streetAddressExceptions.includes(props.form);
  const requiredNumOfSubstrings = streetAddressException ? 3 : 4;
  const requiredLength = streetAddressException ? 16 : 20;
  const addressSubStrings = address.split(',');
  const requiredPostalLength = isAmericanAddress(address) ? 5 : 6;
  if (addressSubStrings.length < requiredNumOfSubstrings || address.length < requiredLength) {
    return t('InvalidAddress');
  }

  if (addressSubStrings.length >= requiredNumOfSubstrings && !getZip(address)) {
    return t('MissingPostalCode');
  }

  if (addressSubStrings.length >= requiredNumOfSubstrings && getZip(address) && getZip(address).length < requiredPostalLength) {
    return t('InvalidPostalOrZipCode');
  }

  return undefined;
};

export const validatePickUpTime = (value) => {
  if (value) {
    return undefined;
  }

  return t('InvalidPickUpTime');
};

export const hoursValidate = (hours) => (hours <= 0 || hours > 12 ? t('InvalidTime') : undefined);

export const minutesValidate = (minutes) => (minutes < 0 || minutes > 59 ? t('InvalidTime') : undefined);

export const isWeekend = (inputDate) => {
  const d = new Date(inputDate);
  d.setMinutes(d.getMinutes() + d.getTimezoneOffset());

  return d.getDay() === 6 || d.getDay() === 0 ? t('IsWeekend') : undefined;
};

export const maxChar40 = maxLength(40);
export const maxChar50 = maxLength(50);
export const maxChar250 = maxLength(250);
export const maxChar255 = maxLength(255);
export const maxChar125 = maxLength(125);
export const maxChar100 = maxLength(100);
export const maxChar75 = maxLength(75);
export const maxChar25 = maxLength(25);
export const maxChar10 = maxLength(10);
export const maxChar7 = maxLength(7);
export const maxChar6 = maxLength(6);
export const maxChar5 = maxLength(5);
export const maxChar4 = maxLength(4);
export const maxChar3 = maxLength(3);
export const maxChar2 = maxLength(2);
export const validateHours = validateIntInRange(1, 12);
export const validateMinutes = validateIntInRange(0, 59);
export const oneTo999 = isInRange(1, 999);
export const isDigitLength4 = isDigitLength(4);
export const isDigitLength6 = isDigitLength(6);
export const isBillToDigitLength10 = (value) => (value && value.length > 0 && value.length < 10 ? t('BillToErrorMessage') : undefined);

export const isUSZip = (value) => {
  const regex = /^\d{5}(-\d{4})?$/;
  return regex.test(value);
};

export const isCANPostal = (value) => {
  const regex = /^([a-zA-Z]\d[a-zA-Z]( )?\d[a-zA-Z]\d)$/;
  return regex.test(value);
};

export const isValidCanadaUSPostalCode = (value) => {
  if (!value) {
    return undefined;
  }

  if (!isUSZip(value) && !isCANPostal(value)) {
    return t('InvalidPostalOrZipCode');
  }

  return undefined;
};

export const measurementValidate = (values) => {
  const errors = {};
  if (values.measurementList && values.measurementList.length) {
    const errorsArray = [];
    const {
      measurementList, division, USShipment, selectedUnits, consigneeCountry, shipperCountry,
    } = values;

    let { measurementType } = values;
    let multiplier = 1;
    if (typeof measurementType === 'string') {
      switch (measurementType) {
        case UnitsOfMeasure.Feet:
          multiplier = 12;
          measurementType = { value: UnitsOfMeasure.Imperial };
          break;
        case UnitsOfMeasure.Centimeters:
          multiplier = 1;
          measurementType = { value: UnitsOfMeasure.Metric };
          break;
        case UnitsOfMeasure.Meters:
          multiplier = 100;
          measurementType = { value: UnitsOfMeasure.Metric };
          break;
        case UnitsOfMeasure.Inches:
        default:
          multiplier = 1;
          measurementType = { value: UnitsOfMeasure.Imperial };
          break;
      }
    }

    const isImperialUnits = selectedUnits ? (selectedUnits.indexOf(t('BtnImperial')) !== -1) : true;
    const weightLimitImperial = (USShipment) ? 19999 : 39999;
    const weightLimitMetric = (USShipment) ? 9071 : 18143;
    const weightLimit = (isImperialUnits) ? weightLimitImperial : weightLimitMetric;
    const weightUnitAbbr = (isImperialUnits) ? ' lbs' : ' kg';

    measurementList.forEach((each, index) => {
      const newMeasurementErrors = {};
      const {
        itemLength, itemWeight, itemWidth, itemHeight,
      } = each;

      if (itemWeight) { // weight
        const curr = Number(getTotalWeight(measurementList));

        if (division === Divisions.Sameday.name) {
          if (curr > weightLimit) {
            newMeasurementErrors.itemWeight = `${t('TotalWeightCannotExceed')} ${numberWithCommas(weightLimit)}${weightUnitAbbr}`;
            errorsArray[index] = newMeasurementErrors;
          }
        }

        if (division === Divisions.Freight.name) {
          if (curr > weightLimit) {
            newMeasurementErrors.itemWeight = `${t('TotalWeightCannotExceed')} ${numberWithCommas(weightLimit)}${weightUnitAbbr}`;
            errorsArray[index] = newMeasurementErrors;
          }
        }
      }

      // for LTL or CS Auth and Non-Auth, and also for US and Non- US shipment/quote/pickup
      if (itemWeight) { // weight
        if (measurementType.value === UnitsOfMeasure.Imperial) {
          // length
          if (consigneeCountry === 'USA' || shipperCountry === 'USA' || consigneeCountry === 'US' || shipperCountry === 'US') {
            if (itemLength && itemLength * multiplier > 239) {
              newMeasurementErrors.itemLength = t('ItemLengthCannotExceed') + (multiplier === 1 ? ` 239 ${t('Inches')}` : ` 20 ${t('Feet')}`);
              errorsArray[index] = newMeasurementErrors;
            }
          } else if (itemLength && itemLength * multiplier > 393) {
            newMeasurementErrors.itemLength = t('ItemLengthCannotExceed') + (multiplier === 1 ? ` 393 ${t('Inches')}` : ` 32.75 ${t('Feet')}`);
            errorsArray[index] = newMeasurementErrors;
          }

          // width
          if (itemWidth && itemWidth * multiplier > 96) {
            newMeasurementErrors.itemWidth = t('ItemWidthCannotExceed') + (multiplier === 1 ? ` 96 ${t('Inches')}` : ` 8 ${t('Feet')}`);
            errorsArray[index] = newMeasurementErrors;
          }

          // height
          if (itemHeight && itemHeight * multiplier > 96) {
            newMeasurementErrors.itemHeight = t('ItemHeightCannotExceed') + (multiplier === 1 ? ` 96 ${t('Inches')}` : ` 8 ${t('Feet')}`);
            errorsArray[index] = newMeasurementErrors;
          }
        } else if (measurementType.value === UnitsOfMeasure.Metric) {
          // length
          if (consigneeCountry === 'USA' || shipperCountry === 'USA' || consigneeCountry === 'US' || shipperCountry === 'US') {
            if (itemLength && itemLength * multiplier > 609) {
              newMeasurementErrors.itemLength = t('ItemLengthCannotExceed') + (multiplier === 1 ? ' 609 cm' : ' 6.09 m');
              errorsArray[index] = newMeasurementErrors;
            }
          } else if (itemLength && itemLength * multiplier > 998) {
            newMeasurementErrors.itemLength = t('ItemLengthCannotExceed') + (multiplier === 1 ? ' 998 cm' : ' 9.98 m');
            errorsArray[index] = newMeasurementErrors;
          }

          // width
          if (itemWidth && itemWidth * multiplier > 243) {
            newMeasurementErrors.itemWidth = t('ItemWidthCannotExceed') + (multiplier === 1 ? ' 243 cm' : ' 2.43 m');
            errorsArray[index] = newMeasurementErrors;
          }

          // height
          if (itemHeight && itemHeight * multiplier > 243) {
            newMeasurementErrors.itemHeight = t('ItemHeightCannotExceed') + (multiplier === 1 ? ' 243 cm' : ' 2.43 m');
            errorsArray[index] = newMeasurementErrors;
          }
        }
      }

      if (errorsArray.length) {
        errors.measurementList = errorsArray;
      }
    });
  }

  return errors;
};

export const closingTimeValidate = (value, formData) => {
  const { closingTime, readyTime } = formData;
  let closingHour = (Number(closingTime.hours) % 12) + (closingTime.minutes / 60);
  let readyHour = (Number(readyTime.hours) % 12) + (readyTime.minutes / 60);
  if (closingTime && closingTime.amOrPm === AMPM.PM) {
    closingHour += 12;
  }

  if (readyTime && readyTime.amOrPm === AMPM.PM) {
    readyHour += 12;
  }

  if (closingTime && closingTime.hours && readyTime && readyTime.hours && (closingHour - readyHour) < 2) {
    return t('AtLeastTwoHours');
  }

  return '';
};

export const readyTimeNotBeforeNow = (value, formData) => {
  const { readyTime, pickupDate } = formData;

  if (
    !readyTime
    || !readyTime.hours
    || !readyTime.minutes
    || !readyTime.amOrPm
  ) {
    return '';
  }

  // Only validate if pickup date is today
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (pickupDate) {
    const selectedDate = new Date(pickupDate);
    selectedDate.setMinutes(selectedDate.getMinutes() + selectedDate.getTimezoneOffset());
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());

    // If pickup date is in the future, no need to validate time
    if (selectedDateOnly > today) {
      return '';
    }
  }

  // Convert current time to total minutes since midnight
  const currentTotalMinutes = (now.getHours() * 60) + now.getMinutes();

  // Convert ready time to 24-hour format
  let readyHours = parseInt(readyTime.hours, 10);
  const readyMinutes = parseInt(readyTime.minutes, 10);

  if (readyTime.amOrPm === AMPM.PM && readyHours !== 12) {
    readyHours += 12;
  } else if (readyTime.amOrPm === AMPM.AM && readyHours === 12) {
    readyHours = 0;
  }

  // Convert ready time to total minutes since midnight
  const readyTotalMinutes = (readyHours * 60) + readyMinutes;

  // Allow times up to 4 hours (240 minutes) earlier than current time
  const maxAllowedPastMinutes = 240;
  if (currentTotalMinutes - readyTotalMinutes > maxAllowedPastMinutes) {
    return t('ReadyTimeCannotBeInPast');
  }

  return '';
};

export const timeIntervalValidate = (value, formData) => {
  const readyDate = new Date();
  const closingDate = new Date();

  const readyTimeHours = parseInt(formData.readyTime.hours, 10);
  const closingTimeHours = parseInt(formData.closingTime.hours, 10);

  readyDate.setHours(formData.readyTime.amOrPm === AMPM.PM ? readyTimeHours + 12 : readyTimeHours);
  readyDate.setMinutes(parseInt(formData.readyTime.minutes, 10));
  readyDate.setSeconds(0);

  closingDate.setHours(formData.closingTime.amOrPm === AMPM.PM ? closingTimeHours + 12 : closingTimeHours);
  closingDate.setMinutes(parseInt(formData.closingTime.minutes, 10));
  closingDate.setSeconds(0);

  return Math.floor(((closingDate - readyDate) / 1000) / 60) < 120 ? t('AtLeastTwoHours') : '';
};

export const dateRangeValidate = (values, allValues) => {
  if (!isValidDateRange({ fromDateString: allValues.filterFromDate, toDateString: allValues.filterToDate })) {
    return t('InvalidDateRange');
  }

  if (getAbsoluteDifferenceInDays({ fromDateString: allValues.filterFromDate, toDateString: allValues.filterToDate }) > MAX_DATE_RANGE_DAYS) {
    return t('DateRangeExceeded', { maxDays: MAX_DATE_RANGE_DAYS });
  }

  return undefined;
};

export const isAfterNovember2020 = (values, allValues, props) => {
  const fromDateLimit = (props._reduxForm && props._reduxForm.fields)
    ? props._reduxForm.fields.fromDateLimit.value
    : props.fields.fromDateLimit.value;
  const fromDateLimitErrorMsg = (props._reduxForm && props._reduxForm.fields)
    ? props._reduxForm.fields.fromDateLimitErrorMsg.value
    : props.fields.fromDateLimitErrorMsg.value;
  if (values) {
    const toDate = new Date(values);
    const november2020 = new Date(fromDateLimit);
    if (toDate < november2020) {
      return fromDateLimitErrorMsg;
    }
  }

  return null;
};

export const isAfterToday = (values, allValues, props) => {
  const dateLimitErrorMsg = (props._reduxForm && props._reduxForm.fields)
    ? props._reduxForm.fields.dateLimitErrorMsg.value
    : props.fields.dateLimitErrorMsg.value;

  if (values) {
    const inputDate = new Date(values);
    const today = new Date();
    if (inputDate > today) {
      return dateLimitErrorMsg;
    }
  }

  return null;
};

export const listCompleted = (list) => list && !list.some((item) => !item.completed);

export const isValidTextEntry = (value) => {
  const regex = /[^a-zA-Z\d\s,:=.]/;
  if (regex.test(value)) {
    return t('InvalidCharacters');
  }

  return undefined;
};

export const isValidDescription = (value) => {
  const regex = /0*(^[0][a-zA-Z\d\s,:=.]*|^0)/g;
  if (regex.test(value) && value.length === 1) {
    return t('InvalidCharacters');
  }

  return undefined;
};

export const isValidDigitInput = (value) => {
  const regex = /0*(^[0][1-9]*|^0)/g;
  if (regex.test(value) && value.length === 1) {
    return t('InvalidCharacters');
  }

  return undefined;
};

export const isValidTextAreaEntry = (value) => {
  const regex = /[^a-zA-Z\d\s,:=.@#$&%]/;
  if (regex.test(value)) {
    return t('InvalidCharacters');
  }

  return undefined;
};

export const isValidWeight = (maxWeight, isImperialUnit) => (value) => {
  if (value > maxWeight) {
    return `${t('TotalWeightError')} ${maxWeight} ${isImperialUnit ? 'lb' : 'kg'}`;
  }

  return undefined;
};

const isReferenceNumberValidEx = (v) => v.match(/^[a-z0-9\s\-\\/]+$/gi);

export const isReferenceNumberValid = (value) => {
  if (value) {
    if (isReferenceNumberValidEx(value)) {
      return undefined;
    }

    return t('TrackShipment_CheckForTypos');
  }

  return undefined;
};

export const maxAddressLine1 = (value) => {
  if (!value) return undefined;

  // Take only address line1 part before first comma
  const addressLine1 = value.split(',')[0]?.trim();

  if (addressLine1.length > 40) {
    return t('AddressLine1Validation');
  }

  return undefined;
};

export const maxChar20 = maxLength(20);
export const maxChar9 = maxLength(9);
export const maxChar30 = maxLength(30);
export const maxChar80 = maxLength(80);

export const isCANPostalvalidation = (value) => {
  const regex = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;
  if (!regex.test(value)) {
    return t('InvalidPostalOrZipCode');
  }

  return undefined;
};

export const isUSZipvalidation = (value) => {
  const regex = /^\d{5}$/;
  if (!regex.test(value)) {
    return t('InvalidPostalOrZipCode');
  }

  return undefined;
};

export const postalZidValidation = (value) => {
  if (!value) return undefined;

  if (isCANPostalvalidation(value)) return undefined;
  if (isUSZipvalidation(value)) return undefined;

  return t('InvalidPostalOrZipCode');
};

export const RMA = (value) => {
  const regex = /^[a-zA-Z0-9]+$/;
  if (!regex.test(value)) {
    return t('InvalidCharacters');
  }

  return undefined;
};

export const originalShipment = (value) => {
  const regex = /^[a-zA-Z][0-9]{8}$/;
  if (!regex.test(value)) {
    return t('InvalidCharacters');
  }

  return undefined;
};

export const noSpace = (value) => {
  const regex = /^\d{0,10}$/;
  if (!regex.test(value)) {
    return t('InvalidCharacters');
  }

  return undefined;
};

export const originalShipmentOrQuoteValidator = (value) => {
  // Keep field optional
  if (!value) {
    return undefined;
  }

  const regex = /^(?:A[0-9]{0,9}|WQ[0-9]{0,8}|Q[0-9]{0,8}|US[0-9]{0,9})$/;

  if (!regex.test(value)) {
    return t('InvalidCharacters');
  }

  return undefined;
};

export const minLength = (min) => (value) => (value && value.length < min ? 'Must be equal or more than 4' : undefined);
export const minChar4 = minLength(4);

export const prohibitedClassNumber = (value) => {
  if (value && String(value) === '6.2') {
    return t('ProhibitedClassNumber');
  }

  return undefined;
};
